import {
  autoUpdate,
  offset as offsetMiddleware,
  flip as flipMiddleware,
  shift as shiftMiddleware,
  size as sizeMiddleware,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  useFloatingNodeId,
  Placement,
  OffsetOptions,
  FlipOptions,
  ShiftOptions,
  useListNavigation,
  useHover,
  UseClickProps,
  UseHoverProps,
} from '@floating-ui/react'
import { useCallback, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'

type UseDropdownMenuParams = {
  opened?: boolean
  defaultOpened?: boolean
  onOpenedChange?: (opened: boolean) => void
  placement?: Placement
  offset?: OffsetOptions
  flip?: FlipOptions
  shift?: ShiftOptions
  inheritWidth?: boolean
  minWidth?: number
  clickProps?: UseClickProps
  hoverProps?: UseHoverProps
}

// eslint-disable-next-line import/prefer-default-export
export function useDropdownMenu({
  opened,
  defaultOpened,
  onOpenedChange,
  placement,
  offset,
  flip,
  shift,
  inheritWidth = true,
  minWidth,
  clickProps,
  hoverProps = { enabled: false },
}: UseDropdownMenuParams = {}) {
  const [isOpened, setIsOpened] = useState(() => opened ?? defaultOpened ?? false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const elementsRef = useRef<Array<HTMLElement | null>>([])

  useUpdateEffect(() => {
    if (typeof opened !== 'undefined') {
      setIsOpened(opened)
    }
  }, [opened])

  const handleOnOpenedChange = useCallback(
    (v: boolean) => {
      if (typeof opened === 'undefined') {
        setIsOpened(v)
      }

      onOpenedChange?.(v)
    },
    [opened, onOpenedChange]
  )

  const nodeId = useFloatingNodeId()

  const { refs, context, floatingStyles, ...floatingProps } = useFloating({
    nodeId,
    placement,
    transform: true,
    open: isOpened,
    onOpenChange: handleOnOpenedChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware(offset),
      flipMiddleware(flip),
      shiftMiddleware(shift),
      sizeMiddleware({
        apply({ rects, elements }) {
          let setMinWidth

          if (inheritWidth) {
            if (minWidth && minWidth > rects.reference.width) {
              setMinWidth = minWidth
            } else {
              setMinWidth = rects.reference.width
            }
          } else if (minWidth) {
            setMinWidth = minWidth
          }

          if (setMinWidth === undefined) return
          Object.assign(elements.floating.style, {
            minWidth: `${setMinWidth}px`,
          })
        },
      }),
    ],
  })

  const click = useClick(context, clickProps)
  const hover = useHover(context, hoverProps)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })

  const listNavigation = useListNavigation(context, {
    loop: true,
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    hover,
    dismiss,
    role,
    listNavigation,
  ])

  const getMenuProps = (props?: React.HTMLProps<HTMLElement>) => {
    return {
      opened: isOpened,
      setOpened: setIsOpened,
      activeIndex,
      setActiveIndex,
      getItemProps,
      elementsRef,
      floatingStyles,
      context,
      nodeId,
      ...getFloatingProps(props),
    }
  }

  return {
    ...floatingProps,
    isOpened,
    setIsOpened: handleOnOpenedChange,
    refs,
    getReferenceProps,
    getMenuProps,
  }
}
